/**
 * Slug -> display-name lookups for categories, brands and subcategories.
 *
 * Product cards, the cart, search results and breadcrumbs all need to turn a
 * relationship slug into a human label, often deep inside render functions
 * where a hook would be awkward. So this stays a plain synchronous API and
 * `SiteDataProvider` pushes live CMS data into it via `registerLabels()` as
 * soon as it loads.
 *
 * The maps are seeded from the bundled defaults, so lookups are correct on the
 * very first render and simply become CMS-accurate a moment later. Components
 * re-render when the provider's state updates, so the newer names are picked up
 * without any extra wiring.
 */

import { CATEGORIES as DEFAULT_CATEGORIES } from "@/data/categories";
import { BRANDS as DEFAULT_BRANDS } from "@/data/brands";

let categoryMap = {};
let brandMap = {};
let subcategoryMap = {};

export function registerLabels({ categories, brands } = {}) {
  if (Array.isArray(categories)) {
    categoryMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));
    subcategoryMap = {};
    categories.forEach((category) => {
      (category.subcategories || []).forEach((sub) => {
        subcategoryMap[sub.slug] = sub.name;
      });
    });
  }
  if (Array.isArray(brands)) {
    brandMap = Object.fromEntries(brands.map((b) => [b.slug, b.name]));
  }
}

// Seed with the bundled content so nothing renders a raw slug on first paint.
registerLabels({ categories: DEFAULT_CATEGORIES, brands: DEFAULT_BRANDS });

/** Fall back to the slug itself — never render "undefined" at a customer. */
export const categoryName = (slug) => categoryMap[slug] || slug;
export const brandName = (slug) => brandMap[slug] || slug;
export const subcategoryName = (slug) => subcategoryMap[slug] || slug;
